import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Popover,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import PauseRoundedIcon from '@mui/icons-material/PauseRounded'
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded'
import HourglassBottomRoundedIcon from '@mui/icons-material/HourglassBottomRounded'
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded'
import VolumeOffRoundedIcon from '@mui/icons-material/VolumeOffRounded'
import TuneRoundedIcon from '@mui/icons-material/TuneRounded'
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded'
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded'
import { useTranslation } from 'react-i18next'
import { fmtTime, FA_ORDER, STAGE_BG, type EmotionKey } from '@/components/researcher/liveDemo/constants'
import { createRelativeFormatter } from '@/components/researcher/studyRows'
import {
  emotionAnalysisService,
  ALL_STIMULI_ID,
  type AnswerFilters,
  type EmotionAnalysisResponse,
  type EmotionStimulus,
  type GazeTrack,
} from '@/services/emotionAnalysisService'
import EmotionTimeline from './EmotionTimeline'
import EmotionStatsPanel from './EmotionStatsPanel'
import QuestionResultsPanel from './QuestionResultsPanel'
import MinRespondentsNotice from './MinRespondentsNotice'
import { TimelineSkeleton, StatsPanelSkeleton } from './EmotionAnalysisSkeleton'
import SwappableStage from './SwappableStage'
import type { HeatField } from './GazeHeatLayer'
import { detectFixations, heatDensity, segmentPeak } from './gazeHeat'
import RespondentFullscreen from './RespondentFullscreen'
import { stageFrameSx } from './stageFrame'
import PrototypeResultsPanel from './PrototypeResultsPanel'
import type { PrototypeReplayData } from './PrototypeReplay'
import { prototypeService, type PrototypeResults } from '@/services/prototypeService'
import { stimulusDrivesClock } from './stimulusVideo'
import { useAuthenticatedVideo } from '@/hooks/useAuthenticatedVideo'
import { authService } from '@/services/authService'
import { recordingService } from '@/services/recordingService'

function stimulusLabel(s: { title: string; type: string; order: number }, fallback: string) {
  return s.title?.trim() || `${fallback} ${s.order + 1}`
}

/** Longest run of empty gaze buckets the dot bridges (a blink, a dropped
 *  frame); a longer gap hides it rather than inventing a path. */
const GAZE_MAX_GAP_MS = 300

/**
 * Gaze position at playhead `ms`, linearly interpolated between the two track
 * samples that bracket it. Returns `null` when there's no gaze track, the
 * playhead is outside the captured range, or the nearest samples are further
 * apart than GAZE_MAX_GAP_MS (so the dot hides).
 */
function gazeAt(gaze: GazeTrack | null | undefined, ms: number): { x: number; y: number } | null {
  const t = gaze?.t_ms
  if (!gaze || !t || t.length === 0) return null
  // Binary search: first index with t[i] >= ms.
  let lo = 0
  let hi = t.length
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if (t[mid] < ms) lo = mid + 1
    else hi = mid
  }
  const valid = (i: number) => gaze.x[i] != null && gaze.y[i] != null
  let r = lo
  while (r < t.length && !valid(r) && t[r] - ms <= GAZE_MAX_GAP_MS) r++
  let l = lo - 1
  while (l >= 0 && !valid(l) && ms - t[l] <= GAZE_MAX_GAP_MS) l--
  const left = l >= 0 && valid(l) && ms - t[l] <= GAZE_MAX_GAP_MS ? l : -1
  const right = r < t.length && valid(r) && t[r] - ms <= GAZE_MAX_GAP_MS ? r : -1
  if (left < 0 && right < 0) return null
  if (left < 0) return { x: gaze.x[right]!, y: gaze.y[right]! }
  if (right < 0 || right === left) return { x: gaze.x[left]!, y: gaze.y[left]! }
  const f = (ms - t[left]) / (t[right] - t[left])
  return {
    x: gaze.x[left]! + (gaze.x[right]! - gaze.x[left]!) * f,
    y: gaze.y[left]! + (gaze.y[right]! - gaze.y[left]!) * f,
  }
}

/** How the gaze heat map reads the clock: off, the last moment around the
 *  playhead (a moving picture — what drew the eye *now*), or the whole
 *  stimulus at once (a still — the classic fixation map). */
type HeatMode = 'off' | 'moment' | 'whole'


export default function EmotionAnalysisPanel({
  projectId,
  initialRespondentId = null,
  filterSlot = null,
}: {
  projectId: number
  /** Drill straight into one respondent (e.g. opened from the respondents table). */
  initialRespondentId?: number | null
  /** When provided, the filter control is portaled here (the tab row) instead of
      sitting in its own row above the charts. */
  filterSlot?: HTMLElement | null
}) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t, i18n } = useTranslation('emotions')
  const { t: tEmo } = useTranslation('liveDemo')
  // Localised emotion labels for the face-pip caption (same source as the timeline legend).
  const faceLabels = useMemo(
    () => Object.fromEntries(FA_ORDER.map((k) => [k, tEmo(`emotions.${k}`)])) as Record<EmotionKey, string>,
    [tEmo],
  )

  const [data, setData] = useState<EmotionAnalysisResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  // Default to the merged "all stimuli" reel; collapses to the lone clip below
  // when a study only has one stimulus (where "Wszystko" would be redundant).
  const [selectedStimulusId, setSelectedStimulusId] = useState<number | null>(ALL_STIMULI_ID)
  const [filters, setFilters] = useState<AnswerFilters>({})
  // Without drill-in the panel never leaves aggregate mode, so a respondent id
  // arriving from the URL is dropped here rather than trusted downstream.
  const [respondentId, setRespondentId] = useState<number | null>(initialRespondentId)

  // Playback clock. A real <video> stimulus owns it (reported via onTick);
  // otherwise a rAF wall-clock below drives it.
  const [currentMs, setCurrentMs] = useState(0)
  const [playing, setPlaying] = useState(false)
  const [videoFailed, setVideoFailed] = useState(false)
  const [muted, setMuted] = useState(false)

  // A monotonic request id guards against a slow in-flight fetch (or poll)
  // landing after the params changed and clobbering fresher data.
  const reqSeq = useRef(0)
  const load = useCallback(
    async (silent: boolean) => {
      const seq = ++reqSeq.current
      if (!silent) {
        setLoading(true)
        setError(false)
      }
      try {
        const res = await emotionAnalysisService.get(projectId, {
          stimulusId: selectedStimulusId,
          filters,
          respondentId,
        })
        if (seq !== reqSeq.current) return
        setData(res)
        // A single-stimulus study has nothing to merge — drop the "Wszystko"
        // default down to that one clip so the UI doesn't offer a pointless reel.
        if (selectedStimulusId === ALL_STIMULI_ID && res.stimuli.length <= 1) {
          setSelectedStimulusId(res.stimuli[0]?.id ?? null)
        } else if (selectedStimulusId == null && res.selected_stimulus_id != null) {
          setSelectedStimulusId(res.selected_stimulus_id)
        }
      } catch {
        if (seq === reqSeq.current && !silent) setError(true)
      } finally {
        if (seq === reqSeq.current && !silent) setLoading(false)
      }
    },
    [projectId, selectedStimulusId, filters, respondentId],
  )

  // Fetch whenever stimulus / filters / drill-in change.
  useEffect(() => {
    void load(false)
  }, [load])

  // While any respondent's recording is still being analysed on the server,
  // quietly re-poll so the curve materialises on its own once the job finishes
  // — no manual refresh needed.
  const anyProcessing = useMemo(
    () => (data?.respondents ?? []).some((r) => r.analysis_status === 'processing'),
    [data?.respondents],
  )
  useEffect(() => {
    if (!anyProcessing) return
    const id = window.setInterval(() => void load(true), 6000)
    return () => window.clearInterval(id)
  }, [anyProcessing, load])

  const durationMs = data?.duration_ms ?? 0

  // Full-screen replay of one respondent (Z-121).
  const [fullscreen, setFullscreen] = useState(false)
  const fullscreenRef = useRef(false)
  fullscreenRef.current = fullscreen

  // Reset the playhead when the active clip changes. In full screen, hopping
  // between respondents keeps the moment (and playback) so reactions to the
  // same instant can be compared person by person.
  const prevStimulusRef = useRef(selectedStimulusId)
  useEffect(() => {
    const stimulusChanged = prevStimulusRef.current !== selectedStimulusId
    prevStimulusRef.current = selectedStimulusId
    if (!stimulusChanged && fullscreenRef.current) return
    setCurrentMs(0)
    setPlaying(false)
    setVideoFailed(false)
  }, [selectedStimulusId, respondentId])

  const stimuli = useMemo(() => data?.stimuli ?? [], [data?.stimuli])
  const selectedStimulus = useMemo(
    () => stimuli.find((s) => s.id === (selectedStimulusId ?? data?.selected_stimulus_id)) ?? null,
    [stimuli, selectedStimulusId, data?.selected_stimulus_id],
  )

  // Merged "all stimuli" reel: the clips play back-to-back along one timeline.
  const segments = useMemo(() => data?.segments ?? null, [data?.segments])
  const merged = !!data?.is_merged && !!segments?.length

  // The clip the playhead currently sits in (merged mode): the last segment whose
  // start is at/behind currentMs. Drives which video the stage shows.
  const activeSegment = useMemo(() => {
    if (!merged || !segments) return null
    let seg = segments[0]
    for (const s of segments) {
      if (currentMs >= s.start_ms) seg = s
      else break
    }
    return seg
  }, [merged, segments, currentMs])

  // What the stage actually renders/plays: a synthetic stimulus for the active
  // segment in merged mode, otherwise the picked stimulus. Its clock is local to
  // the clip (currentMs rebased by the segment's start offset).
  const stageStimulus: EmotionStimulus | null = useMemo(() => {
    if (!merged) return selectedStimulus
    if (!activeSegment) return null
    return {
      id: activeSegment.stimulus_id,
      order: activeSegment.order,
      title: activeSegment.title,
      type: activeSegment.type,
      url: activeSegment.url,
      payload: activeSegment.payload,
      exposure_ms: activeSegment.duration_ms,
    }
  }, [merged, activeSegment, selectedStimulus])
  const stageCurrentMs = merged && activeSegment ? currentMs - activeSegment.start_ms : currentMs

  // A failed clip falls back to the wall-clock; reset that flag per segment so a
  // single broken clip in the reel doesn't disable video for all the others.
  useEffect(() => {
    setVideoFailed(false)
  }, [activeSegment?.stimulus_id])

  // When the stimulus renders as a real <video> (or a YouTube player), the
  // player owns the clock (the stage reports its time via onTick) — a
  // wall-clock can't run ahead of a buffering video without degrading playback
  // into a seek storm.
  const videoDrivesClock = !videoFailed && stimulusDrivesClock(stageStimulus)

  const handleVideoTick = useCallback(
    (ms: number) => {
      // The stage reports clip-local time; rebase to the merged timeline. Clamp
      // within the clip so the playhead doesn't spill into the next segment — the
      // onEnded handler owns the hop to the following clip.
      if (merged && activeSegment) {
        const local = Math.min(ms, Math.max(0, activeSegment.duration_ms - 1))
        const global = activeSegment.start_ms + local
        const segEnd = activeSegment.start_ms + activeSegment.duration_ms
        if (durationMs > 0 && global >= durationMs) {
          setPlaying(false)
          setCurrentMs(durationMs)
        } else {
          // A last tick of the finished clip can land after onEnded has already
          // hopped to the next one; taking it would pull the playhead back across
          // the boundary, remount the old clip, end it again — an endless swap
          // loop that froze the reel at the cut and made the panels flicker.
          setCurrentMs((prev) => (prev >= segEnd && global < segEnd ? prev : global))
        }
        return
      }
      if (durationMs > 0 && ms >= durationMs) {
        setPlaying(false)
        setCurrentMs(durationMs)
      } else {
        setCurrentMs(ms)
      }
    },
    [merged, activeSegment, durationMs],
  )

  const handleVideoEnded = useCallback(() => {
    // Merged reel: a clip ending advances to the next one (keep playing), or
    // stops at the very end. Single mode just parks the playhead at the end.
    if (merged && segments && activeSegment) {
      const idx = segments.indexOf(activeSegment)
      const next = segments[idx + 1]
      if (next) setCurrentMs(next.start_ms)
      else {
        setPlaying(false)
        setCurrentMs(durationMs)
      }
      return
    }
    setPlaying(false)
    setCurrentMs(durationMs)
  }, [merged, segments, activeSegment, durationMs])

  // rAF clock — drives currentMs only for stimuli without a playable video
  // (image / text / placeholder).
  const rafRef = useRef<number>(0)
  useEffect(() => {
    if (!playing || durationMs <= 0 || videoDrivesClock) return
    let last = performance.now()
    const tick = (now: number) => {
      const dt = now - last
      last = now
      setCurrentMs((ms) => {
        const next = ms + dt
        if (next >= durationMs) {
          setPlaying(false)
          return durationMs
        }
        return next
      })
      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
  }, [playing, durationMs, videoDrivesClock])

  const togglePlay = useCallback(() => {
    if (durationMs <= 0) return
    setPlaying((p) => {
      if (!p && currentMs >= durationMs) setCurrentMs(0)
      return !p
    })
  }, [durationMs, currentMs])

  const replay = useCallback(() => {
    setCurrentMs(0)
    setPlaying(true)
  }, [])

  // Active series: a respondent's curve when drilled in, else the aggregate.
  // When drilled into a respondent the backend returns no individual for (e.g.
  // they never finished watching, so there's no recording), DON'T silently fall
  // back to the aggregate — that would show the group's curve under this one
  // respondent's name, looking like another person's results.
  const drilledIn = respondentId != null
  const series = drilledIn ? data?.individual?.series ?? null : data?.aggregate
  const activeRespondent = data?.respondents.find((r) => r.id === respondentId) ?? null
  // Identify the drilled-in respondent by when they took part, not the R-#id.
  const activeRespondentLabel = activeRespondent
    ? activeRespondent.captured_at
      ? createRelativeFormatter(i18n.language)(activeRespondent.captured_at)
      : activeRespondent.label
    : undefined
  // Drilled in, but this respondent has no analysable recording for the stimulus.
  const respondentNoData = drilledIn && !data?.individual

  // Face picture-in-picture: when drilled into one respondent, fetch their face
  // recording (authenticated → blob) and overlay it on the stimulus, synced to
  // the playhead via the stimulus's offset within the recording.
  const individual = respondentId != null ? data?.individual ?? null : null
  const faceRecordingUrl = individual?.recording_url ?? null
  const { videoUrl: faceUrl, loadVideo: loadFace, clearVideo: clearFace } = useAuthenticatedVideo()
  useEffect(() => {
    if (!faceRecordingUrl) {
      clearFace()
      return
    }
    const token = authService.getAccessToken()
    if (token) void loadFace(faceRecordingUrl, token)
  }, [faceRecordingUrl, loadFace, clearFace])

  // Gaze dot (eye-tracking studies): the respondent's per-frame gaze position
  // (normalised 0..1) resampled onto grid_ms, interpolated to the current
  // playhead and drawn over the stimulus player by SwappableStage. Only in
  // drill-in mode — gaze is one person's, never an aggregate.
  // Figma prototype (Z-98): its own per-screen results, and for one respondent
  // the replay the stage plays next to their face. The viewport gaze dot stays
  // off here — the stage shows a screen picture, so the replay draws its own
  // dot and heat map from gaze the backend already mapped onto that picture.
  const figmaStimulusId = !merged && selectedStimulus?.type === 'figma' ? selectedStimulus.id : null
  const figmaStimulus = figmaStimulusId != null ? selectedStimulus : null
  const [prototype, setPrototype] = useState<PrototypeResults | null>(null)
  useEffect(() => {
    if (figmaStimulusId == null) {
      setPrototype(null)
      return
    }
    let cancelled = false
    prototypeService
      .results(projectId, { stimulusId: figmaStimulusId, respondentId, filters })
      .then((res) => {
        if (!cancelled) setPrototype(res)
      })
      .catch(() => {
        if (!cancelled) setPrototype(null)
      })
    return () => {
      cancelled = true
    }
  }, [projectId, figmaStimulusId, respondentId, filters])
  const prototypeReplay = useMemo<PrototypeReplayData | null>(() => {
    if (!prototype || figmaStimulusId == null) return null
    const screens = Object.fromEntries(prototype.screens.map((sc) => [sc.node_id, sc]))
    const start = prototype.screens.find((sc) => sc.is_start)?.node_id ?? prototype.screens[0]?.node_id ?? ''
    return { screens, startNodeId: start, session: drilledIn ? prototype.individual : null }
  }, [prototype, figmaStimulusId, drilledIn])

  const gazeTrack = drilledIn && !figmaStimulus ? data?.individual?.gaze ?? null : null
  const hasGaze = useMemo(
    () => !!gazeTrack && gazeTrack.x.some((v) => v != null),
    [gazeTrack],
  )
  const gazePoint = useMemo(
    () => gazeAt(gazeTrack, currentMs),
    [gazeTrack, currentMs],
  )

  // Gaze heat map (eye-tracking studies): the group's pooled gaze, or — drilled
  // into one respondent — just theirs. A still (image, text) defaults to the
  // whole exposure, a moving picture to the moment behind the playhead.
  const heatTrack = figmaStimulus ? null : drilledIn ? gazeTrack : data?.gaze_heat ?? null
  const hasHeat = useMemo(() => !!heatTrack && heatTrack.x.some((v) => v != null), [heatTrack])
  const stillStimulus = stageStimulus?.type === 'image' || stageStimulus?.type === 'text'
  const [heatChoice, setHeatChoice] = useState<HeatMode | null>(null)
  const heatMode: HeatMode = heatChoice ?? (stillStimulus ? 'whole' : 'moment')
  const segStart = merged && activeSegment ? activeSegment.start_ms : 0
  const segEnd = merged && activeSegment ? activeSegment.start_ms + activeSegment.duration_ms : durationMs
  // Fixations of this segment only, so none straddles a cut between clips;
  // the colour scale is the segment's hottest moment (see gazeHeat.ts).
  const heatFixations = useMemo(
    () => (heatTrack && hasHeat ? detectFixations(heatTrack, segStart, segEnd) : null),
    [heatTrack, hasHeat, segStart, segEnd],
  )
  const heatPeak = useMemo(
    () => (heatFixations && heatMode !== 'off' ? segmentPeak(heatFixations, heatMode, segStart, segEnd) : 0),
    [heatFixations, heatMode, segStart, segEnd],
  )
  // The moment window moves in 50 ms steps (the one-person gaze bin), so the
  // map redraws twenty times a second rather than on every playhead tick.
  const heatTick = heatMode === 'whole' ? segEnd : Math.floor(currentMs / 50) * 50
  const heatField = useMemo<HeatField | null>(() => {
    if (!heatFixations || heatMode === 'off' || heatPeak <= 0) return null
    return { density: heatDensity(heatFixations, heatMode, heatTick, segStart, segEnd), peak: heatPeak }
  }, [heatFixations, heatMode, heatPeak, heatTick, segStart, segEnd])

  const filterCount = Object.values(filters).reduce((n, v) => n + v.length, 0)
  // Stable callbacks so the memoized side panels below don't re-render on every
  // ~30 Hz playhead tick (only the player + timeline should churn during playback).
  const clearFilters = useCallback(() => setFilters({}), [])
  const selectRespondent = useCallback((id: number | null) => setRespondentId(id), [])
  const toggleMute = useCallback(() => setMuted((m) => !m), [])
  const scrub = useCallback(
    (ms: number) => {
      setPlaying(false)
      setCurrentMs(Math.max(0, Math.min(ms, durationMs)))
    },
    [durationMs],
  )
  // Opened from the group average, the view starts on the first respondent
  // with data — full screen is always one person's face.
  const firstWithData = data?.respondents.find((r) => r.has_data)?.id ?? null
  const openFullscreen = useCallback(() => {
    if (respondentId == null) {
      if (firstWithData == null) return
      setRespondentId(firstWithData)
    }
    setFullscreen(true)
  }, [respondentId, firstWithData])

  // Per-respondent "retry analysis" — re-dispatches the failed job, then silently
  // reloads so the row flips to "analysing…" and the poll (below) resumes on its
  // own. `retryingIds` tracks in-flight retries to show a spinner / disable the
  // button between the click and that reload landing.
  const [retryingIds, setRetryingIds] = useState<Set<number>>(new Set())
  const retryRespondent = useCallback(
    async (respId: number, recordingId: number) => {
      setRetryingIds((prev) => new Set(prev).add(respId))
      try {
        await recordingService.startAnalysis(recordingId)
        await load(true)
      } catch {
        // Leave the row in its failed state; the button re-enables so the
        // researcher can try again.
      } finally {
        setRetryingIds((prev) => {
          const next = new Set(prev)
          next.delete(respId)
          return next
        })
      }
    },
    [load],
  )

  if (loading && !data) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 320 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
        <Typography sx={{ color: c.ink72 }}>{t('error')}</Typography>
      </Paper>
    )
  }

  if (!stimuli.length) {
    return (
      <Paper variant="outlined" sx={{ p: 5, textAlign: 'center', borderRadius: 3 }}>
        <Typography sx={{ fontWeight: 700, mb: 0.5 }}>{t('empty.title')}</Typography>
        <Typography sx={{ color: c.ink56, fontSize: 14 }}>{t('empty.body')}</Typography>
      </Paper>
    )
  }

  // Under the edition's minimum sample the backend suppresses every curve on
  // purpose, so there is nothing here to draw — show the running count and what
  // is still missing instead. `min_respondents` is null on Pro, which has no
  // floor and keeps showing partial data.
  //
  // Rendered below the filter control rather than instead of it. The count the
  // backend sends is the count *after* filtering, so narrowing to "women, 25-34"
  // on a study with thirty responses can land under the floor — and returning
  // here before the control is mounted would take away the only way back out.
  const belowFloor = Boolean(data?.below_min_respondents && data?.min_respondents != null)

  // Empty curve: the drilled-in respondent has nothing, or (aggregate) no
  // respondent matches. A drilled-in respondent still mid-analysis shows the
  // processing hint rather than the flat "no data" line.
  const noData = respondentNoData || (data?.respondent_count ?? 0) === 0
  const respondentProcessing = activeRespondent?.analysis_status === 'processing'

  // One control to pick the clip (or the merged reel) + tuck the rarely-used
  // demographic filters behind the same dropdown. Portaled into the tab row when
  // a slot is provided; otherwise rendered inline above the charts.
  const filterMenu = (
    <FilterMenu
      data={data}
      filters={filters}
      setFilters={setFilters}
      filterCount={filterCount}
      onClear={clearFilters}
      stimuli={stimuli}
      selectedStimulusId={selectedStimulusId}
      onSelectStimulus={setSelectedStimulusId}
    />
  )

  const filterControl = filterSlot ? (
    createPortal(filterMenu, filterSlot)
  ) : (
    <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
      {filterMenu}
    </Stack>
  )

  if (belowFloor) {
    return (
      <Stack spacing={2.25}>
        {filterControl}
        <MinRespondentsNotice
          count={data!.respondent_count}
          min={data!.min_respondents!}
          filtered={filterCount > 0}
          onClearFilters={clearFilters}
        />
      </Stack>
    )
  }

  return (
    <Stack spacing={2.25}>
      {filterControl}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.5fr 1fr' }, gap: 2.25, alignItems: 'start' }}>
        {/* Player + timeline */}
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
          <Stack spacing={1.5}>
            {/* FaceTime-style swap: clicking the face pip enlarges it (with the
                emotion rectangle/colors) and tucks the stimulus into the corner. */}
            {/* While the full-screen view is open it owns the players: a second
                stimulus <video> here would double the audio and fight over the clock. */}
            {fullscreen ? (
              <Box sx={{ ...stageFrameSx, bgcolor: STAGE_BG, borderRadius: 2 }} />
            ) : (
              <SwappableStage
                stimulus={stageStimulus}
                stageCurrentMs={stageCurrentMs}
                playing={playing}
                onTick={handleVideoTick}
                onEnded={handleVideoEnded}
                onVideoFailed={() => setVideoFailed(true)}
                muted={muted}
                label={stageStimulus ? stimulusLabel(stageStimulus, t('stimulusFallback')) : ''}
                showFace={!loading && !!faceRecordingUrl}
                faceSrc={faceUrl}
                // The face recording is one continuous video; `currentMs` here
                // is the (merged) panel clock, so the offset must rebase it onto
                // the recording. Single mode: just the stimulus's window start.
                // Merged mode: the active clip's window start minus where that
                // clip sits in the merged timeline, so `offsetMs + currentMs`
                // lands on `window_start + clip-local` — synced clip-by-clip,
                // while the emotion caption still reads the global playhead.
                faceCurrentMs={currentMs}
                faceOffsetMs={
                  merged && activeSegment
                    ? activeSegment.window_start_ms - activeSegment.start_ms
                    : individual?.window_start_ms ?? 0
                }
                series={series}
                gridMs={data?.grid_ms}
                faceLabels={faceLabels}
                gazePoint={gazePoint}
                heatField={heatField}
                prototypeReplay={prototypeReplay}
              />
            )}

            {/* Transport bar */}
            <Stack direction="row" spacing={1.5} alignItems="center">
              <IconButton onClick={togglePlay} color="primary" sx={{ bgcolor: c.coralSoft }}>
                {playing ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />}
              </IconButton>
              <IconButton onClick={replay} size="small">
                <ReplayRoundedIcon fontSize="small" />
              </IconButton>
              <IconButton onClick={() => setMuted((m) => !m)} size="small" aria-label={muted ? t('unmute') : t('mute')}>
                {muted ? <VolumeOffRoundedIcon fontSize="small" /> : <VolumeUpRoundedIcon fontSize="small" />}
              </IconButton>
              <Typography sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 13, color: c.ink72 }}>
                {fmtTime(currentMs / 1000)} / {fmtTime(durationMs / 1000)}
              </Typography>
              {merged && activeSegment && (
                <Typography
                  sx={{ fontSize: 12, fontWeight: 700, color: c.ink72, maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  title={stimulusLabel(activeSegment, t('stimulusFallback'))}
                >
                  · {stimulusLabel(activeSegment, t('stimulusFallback'))}
                </Typography>
              )}
              <Box sx={{ flex: 1 }} />
              {data?.timeline_estimated && (
                <Tooltip title={t('approxTimelineHint')}>
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    icon={<WarningAmberRoundedIcon />}
                    label={t('approxTimeline')}
                    sx={{ fontWeight: 600 }}
                  />
                </Tooltip>
              )}
              {firstWithData != null && (
                <Button
                  size="small"
                  onClick={openFullscreen}
                  startIcon={<OpenInFullRoundedIcon sx={{ fontSize: 16 }} />}
                  title={t('fullscreen.openHint')}
                  sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 99, px: 1.25, color: c.ink, bgcolor: c.gray50, '&:hover': { bgcolor: c.gray100 } }}
                >
                  {t('fullscreen.open')}
                </Button>
              )}
              <Chip
                size="small"
                icon={respondentId != null ? <PersonOutlineRoundedIcon /> : <GroupsRoundedIcon />}
                label={
                  respondentId != null
                    ? activeRespondentLabel ?? t('individual')
                    : t('aggregateOf', { count: data?.respondent_count ?? 0 })
                }
                sx={{ fontWeight: 600 }}
              />
            </Stack>

            {loading ? (
              <TimelineSkeleton />
            ) : (
              <>
                {series && (
                  <EmotionTimeline
                    series={series}
                    gridMs={data?.grid_ms ?? []}
                    durationMs={durationMs}
                    currentMs={currentMs}
                    segments={merged ? segments?.map((s) => ({ start_ms: s.start_ms, title: stimulusLabel(s, t('stimulusFallback')) })) : undefined}
                    onScrub={scrub}
                  />
                )}
                {noData && (respondentNoData ? respondentProcessing : anyProcessing) && (
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ py: 1 }}>
                    <CircularProgress size={14} thickness={5} sx={{ color: c.coral }} />
                    <Typography sx={{ color: c.ink56, fontSize: 13, textAlign: 'center' }}>
                      {t('analyzingData')}
                    </Typography>
                  </Stack>
                )}
                {noData && (respondentNoData ? !respondentProcessing : !anyProcessing) && (
                  <Typography sx={{ color: c.ink56, fontSize: 13, textAlign: 'center', py: 1 }}>
                    {respondentNoData ? t('noRespondentData') : t('noMatchingData')}
                  </Typography>
                )}
              </>
            )}
            {/* Eye-tracking hint: the gaze dot is drawn over the stimulus player
                (SwappableStage), synced to the playhead. Shown when the drilled-in
                respondent has captured gaze. */}
            {hasHeat && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ pt: 0.5, flexWrap: 'wrap', rowGap: 0.75 }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: c.ink72 }}>{t('eyeTracking.heatmap')}</Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={heatMode}
                  onChange={(_, v: HeatMode | null) => v && setHeatChoice(v)}
                  sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 1.25, fontSize: 12, textTransform: 'none', fontWeight: 600 } }}
                >
                  <ToggleButton value="moment">{t('eyeTracking.heatMoment')}</ToggleButton>
                  <ToggleButton value="whole">{t('eyeTracking.heatWhole')}</ToggleButton>
                  <ToggleButton value="off">{t('eyeTracking.heatOff')}</ToggleButton>
                </ToggleButtonGroup>
                <Box
                  aria-hidden
                  sx={{
                    width: 72,
                    height: 8,
                    borderRadius: 99,
                    background: 'linear-gradient(90deg, rgb(0,90,255), rgb(0,220,220), rgb(60,220,60), rgb(255,230,0), rgb(255,30,0))',
                  }}
                />
                <Typography sx={{ fontSize: 12, color: c.ink56 }}>
                  {drilledIn ? t('eyeTracking.heatLegendOne') : t('eyeTracking.heatLegendGroup', { count: data?.respondent_count ?? 0 })}
                </Typography>
              </Stack>
            )}
            {drilledIn && hasGaze && (
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ pt: 0.5 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'rgba(255,64,64,0.55)', border: '2px solid rgba(255,255,255,0.95)', flexShrink: 0 }} />
                <Typography sx={{ fontSize: 12.5, color: c.ink56 }}>
                  {t('eyeTracking.hint', {
                    defaultValue:
                      'Przybliżony punkt patrzenia respondenta naniesiony na materiał (orientacyjny).',
                  })}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Paper>

        {/* Respondents (who am I looking at) sits above the emotion stats it drives. */}
        <Stack spacing={2.25}>
          <RespondentPanel
            data={data}
            respondentId={respondentId}
            onSelect={selectRespondent}
            onRetry={retryRespondent}
            retryingIds={retryingIds}
          />
          {loading ? (
            <StatsPanelSkeleton />
          ) : (
            <EmotionStatsPanel series={noData ? null : series} gridMs={data?.grid_ms ?? []} currentMs={currentMs} />
          )}
        </Stack>
      </Box>

      {fullscreen && (
        <RespondentFullscreen
          open
          onClose={() => setFullscreen(false)}
          stimulus={stageStimulus}
          stimulusLabel={stageStimulus ? stimulusLabel(stageStimulus, t('stimulusFallback')) : ''}
          stageCurrentMs={stageCurrentMs}
          playing={playing}
          muted={muted}
          onTick={handleVideoTick}
          onEnded={handleVideoEnded}
          onVideoFailed={() => setVideoFailed(true)}
          onTogglePlay={togglePlay}
          onToggleMute={toggleMute}
          prototypeReplay={prototypeReplay}
          gazePoint={gazePoint}
          heatField={heatField}
          faceSrc={faceUrl}
          faceLoading={loading || (!!faceRecordingUrl && !faceUrl)}
          faceCurrentMs={currentMs}
          faceOffsetMs={
            merged && activeSegment
              ? activeSegment.window_start_ms - activeSegment.start_ms
              : individual?.window_start_ms ?? 0
          }
          noData={respondentNoData}
          series={series}
          gridMs={data?.grid_ms ?? []}
          currentMs={currentMs}
          durationMs={durationMs}
          onScrub={scrub}
          respondents={data?.respondents ?? []}
          respondentId={respondentId}
          onSelectRespondent={selectRespondent}
          emotionLabels={faceLabels}
        />
      )}

      {figmaStimulus && prototype && <PrototypeResultsPanel results={prototype} />}

      {/* Survey answer results — distribution per question (filtered set) or the
          drilled-in respondent's own answers. The right-hand filters narrow it. */}
      <QuestionResultsPanel
        questions={data?.questions ?? []}
        distribution={data?.answer_distribution ?? {}}
        respondentAnswers={data?.respondent_answers ?? null}
        respondentId={respondentId}
        respondentLabel={activeRespondentLabel}
        respondentCount={data?.total_respondent_count ?? 0}
      />
    </Stack>
  )
}

// ───────────────────────── Filters ─────────────────────────

const FilterMenu = memo(function FilterMenu({
  data,
  filters,
  setFilters,
  filterCount,
  onClear,
  stimuli,
  selectedStimulusId,
  onSelectStimulus,
}: {
  data: EmotionAnalysisResponse | null
  filters: AnswerFilters
  setFilters: (f: AnswerFilters) => void
  filterCount: number
  onClear: () => void
  stimuli: EmotionStimulus[]
  selectedStimulusId: number | null
  onSelectStimulus: (id: number | null) => void
}) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('emotions')
  const facets = data?.filters ?? []
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const toggle = (qid: number, value: number) => {
    const current = filters[qid] ?? []
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value]
    const updated = { ...filters }
    if (next.length) updated[qid] = next
    else delete updated[qid]
    setFilters(updated)
  }

  // The clip picker only makes sense with more than one stimulus to choose from.
  const showStimulusPicker = stimuli.length > 1
  // With a single stimulus and no demographic facets there's nothing to offer.
  if (!showStimulusPicker && !facets.length) return null

  const open = Boolean(anchorEl)
  const isAll = selectedStimulusId === ALL_STIMULI_ID
  // What the trigger button advertises: the current view, meaning the merged
  // reel or the one clip. With a single stimulus there is nothing to pick, so
  // the label falls through to the filters.
  const selectedStimulus = stimuli.find((s) => s.id === selectedStimulusId) ?? null
  const triggerLabel = isAll
      ? t('allStimuli')
      : selectedStimulus
        ? stimulusLabel(selectedStimulus, t('stimulusFallback'))
        : t('filters.title')

  const pick = (id: number | null) => {
    onSelectStimulus(id)
    setAnchorEl(null)
  }

  return (
    <>
      <Badge
        badgeContent={filterCount}
        color="primary"
        overlap="circular"
        sx={{ flexShrink: 0, '& .MuiBadge-badge': { fontWeight: 700, fontSize: 10, minWidth: 18, height: 18 } }}
      >
        <Button
          onClick={(e) => setAnchorEl(e.currentTarget)}
          startIcon={<TuneRoundedIcon />}
          variant="outlined"
          color="inherit"
          disableElevation
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 700,
            maxWidth: 280,
            color: c.ink,
            borderColor: c.ink08,
            bgcolor: c.white,
            '& .MuiButton-startIcon': { color: c.coral },
            '&:hover': { borderColor: c.ink32, bgcolor: c.gray50 },
          }}
        >
          <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {triggerLabel}
          </Box>
        </Button>
      </Badge>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { mt: 1, borderRadius: 3, width: 320, maxHeight: 460, boxShadow: '0 12px 40px rgba(27,28,56,.16)' } } }}
      >
        <Box sx={{ p: 2.25 }}>
          {showStimulusPicker && (
            <Box sx={{ mb: facets.length ? 2 : 0 }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: c.ink56, mb: 0.75, letterSpacing: '.02em' }}>
                {t('stimulusSection')}
              </Typography>
              <Stack spacing={0.5}>
                <StimulusOption
                  label={t('allStimuli')}
                  hint={t('allStimuliHint')}
                  active={isAll}
                  onClick={() => pick(ALL_STIMULI_ID)}
                />
                {stimuli.map((s) => (
                  <StimulusOption
                    key={s.id}
                    label={stimulusLabel(s, t('stimulusFallback'))}
                    active={s.id === selectedStimulusId}
                    onClick={() => pick(s.id)}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {facets.length > 0 && (
            <>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <SectionTitle icon={<TuneRoundedIcon fontSize="small" />} title={t('filters.title')} />
                {filterCount > 0 && (
                  <Button size="small" onClick={onClear} sx={{ minWidth: 0, textTransform: 'none' }}>
                    {t('filters.clear')}
                  </Button>
                )}
              </Stack>
              <Stack spacing={1.75} sx={{ mt: 1.5 }}>
                {facets.map((f) => (
                  <Box key={f.id}>
                    <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: c.ink56, mb: 0.75, letterSpacing: '.02em' }}>
                      {f.text}
                      {f.is_meta && (
                        <Box component="span" sx={{ ml: 0.75, fontSize: 10, color: c.ink48 }}>
                          · {t('filters.meta')}
                        </Box>
                      )}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      {f.choices.map((choice) => {
                        const on = (filters[f.id] ?? []).includes(choice.value)
                        return (
                          <Chip
                            key={choice.value}
                            label={choice.text}
                            size="small"
                            onClick={() => toggle(f.id, choice.value)}
                            variant={on ? 'filled' : 'outlined'}
                            color={on ? 'primary' : 'default'}
                            sx={{ borderRadius: 1.5, fontWeight: on ? 700 : 500 }}
                          />
                        )
                      })}
                    </Box>
                  </Box>
                ))}
              </Stack>
            </>
          )}
        </Box>
      </Popover>
    </>
  )
})

/** A single-select clip option inside the picker dropdown (radio-style). */
function StimulusOption({
  label,
  hint,
  active,
  onClick,
}: {
  label: string
  hint?: string
  active: boolean
  onClick: () => void
}) {
  const theme = useTheme()
  const c = theme.affexy.colors
  return (
    <Box
      onClick={onClick}
      role="button"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1,
        py: 0.75,
        borderRadius: 2,
        cursor: 'pointer',
        bgcolor: active ? c.coralSoft : c.gray50,
        border: `1px solid ${active ? c.coral : 'transparent'}`,
        transition: 'background-color .12s, border-color .12s',
        '&:hover': { bgcolor: active ? c.coralSoft : c.gray100 },
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: c.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {label}
        </Typography>
        {hint && <Typography sx={{ fontSize: 11, color: c.ink56 }}>{hint}</Typography>}
      </Box>
      <Box
        sx={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          flexShrink: 0,
          display: 'grid',
          placeItems: 'center',
          bgcolor: active ? c.coral : 'transparent',
          border: `1.5px solid ${active ? c.coral : c.ink32}`,
          color: c.white,
        }}
      >
        {active && <CheckRoundedIcon sx={{ fontSize: 12 }} />}
      </Box>
    </Box>
  )
}

// ───────────────────────── Respondents (drill-in) ─────────────────────────

const RespondentPanel = memo(function RespondentPanel({
  data,
  respondentId,
  onSelect,
  onRetry,
  retryingIds,
}: {
  data: EmotionAnalysisResponse | null
  respondentId: number | null
  onSelect: (id: number | null) => void
  onRetry: (respondentId: number, recordingId: number) => void
  retryingIds: Set<number>
}) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t, i18n } = useTranslation('emotions')
  const rel = createRelativeFormatter(i18n.language)
  const respondents = data?.respondents ?? []
  const withData = respondents.filter((r) => r.has_data)
  const processingCount = respondents.filter((r) => r.analysis_status === 'processing').length
  const aggregateMode = respondentId == null

  return (
    <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.25 }}>
        <SectionTitle icon={<PersonOutlineRoundedIcon fontSize="small" />} title={t('respondents.title')} />
        <Typography sx={{ color: c.ink56, fontSize: 12, fontWeight: 600 }}>
          {t('respondents.count', { withData: withData.length, total: respondents.length })}
        </Typography>
      </Stack>

      {processingCount > 0 && (
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.75 }}>
          <CircularProgress size={11} thickness={5} sx={{ color: c.coral }} />
          <Typography sx={{ color: c.coral, fontSize: 11.5, fontWeight: 600 }}>
            {t('respondents.analyzingCount', { count: processingCount })}
          </Typography>
        </Stack>
      )}

      <Stack spacing={0.5} sx={{ mt: 1.25 }}>
        {/* What the curve shows: the whole group's average, or one person. */}
        <SelectRow
          selectable
          active={aggregateMode}
          badge={
            <Box sx={{ width: 30, height: 30, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: aggregateMode ? c.coral : c.coralSoft, color: aggregateMode ? c.white : c.coral, flexShrink: 0 }}>
              <GroupsRoundedIcon sx={{ fontSize: 17 }} />
            </Box>
          }
          title={t('respondents.aggregate')}
          subtitle={t('respondents.aggregateHint', { count: withData.length })}
          onClick={() => onSelect(null)}
        />

        {respondents.length > 0 && (
          <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: c.ink48, mt: 0.75, mb: 0.25, px: 0.25 }}>
            {t('respondents.individualLabel')}
          </Typography>
        )}

        <Stack spacing={0.5} sx={{ maxHeight: 264, overflow: 'auto', mx: -0.25, px: 0.25 }}>
          {respondents.map((r) => {
            const active = r.id === respondentId
            const retrying = retryingIds.has(r.id)
            // A just-retried row still reports 'failed' until the reload lands —
            // show it as analysing meanwhile so the retry reads as taking effect.
            const processing = r.analysis_status === 'processing' || retrying
            const failed = r.analysis_status === 'failed' && !retrying
            // Held on an empty minutes package — the recording is safe, so this
            // must not read as "no data" and send anyone debugging.
            const blocked = r.analysis_status === 'blocked' && !retrying
            const demo = Object.values(r.demographics).join(' · ')
            // Identify by face avatar + when they took part, not the R-#id.
            const when = r.captured_at ? rel(r.captured_at) : r.label
            return (
              <SelectRow
                key={r.id}
                selectable={r.has_data}
                active={active}
                disabled={!r.has_data}
                badge={
                  <Avatar
                    src={r.thumbnail_url || undefined}
                    sx={{ width: 30, height: 30, bgcolor: active ? c.coral : c.gray100, color: active ? c.white : c.ink56, flexShrink: 0, fontSize: 12, fontWeight: 800 }}
                  >
                    <PersonOutlineRoundedIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                }
                title={when}
                subtitle={demo || undefined}
                onClick={() => r.has_data && onSelect(active ? null : r.id)}
                status={
                  processing ? (
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0, ml: 1 }}>
                      <CircularProgress size={11} thickness={5} sx={{ color: c.coral }} />
                      <Typography sx={{ fontSize: 10.5, color: c.coral, fontWeight: 600 }}>
                        {t('respondents.analyzing')}
                      </Typography>
                    </Stack>
                  ) : failed ? (
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0, ml: 1 }}>
                      <Tooltip title={t('respondents.failedHint')}>
                        <Stack direction="row" spacing={0.4} alignItems="center">
                          <WarningAmberRoundedIcon sx={{ fontSize: 13, color: c.danger }} />
                          <Typography sx={{ fontSize: 10.5, color: c.danger, fontWeight: 700 }}>
                            {t('respondents.failed')}
                          </Typography>
                        </Stack>
                      </Tooltip>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        disabled={r.recording_id == null}
                        startIcon={<ReplayRoundedIcon sx={{ fontSize: 14 }} />}
                        onClick={(e) => {
                          e.stopPropagation()
                          if (r.recording_id != null) onRetry(r.id, r.recording_id)
                        }}
                        sx={{ minWidth: 0, py: 0.1, px: 0.9, fontSize: 10.5, fontWeight: 700, textTransform: 'none', borderRadius: 1.5 }}
                      >
                        {t('respondents.retry')}
                      </Button>
                    </Stack>
                  ) : blocked ? (
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0, ml: 1 }}>
                      <Tooltip title={t('respondents.blockedHint')}>
                        <Stack direction="row" spacing={0.4} alignItems="center">
                          <HourglassBottomRoundedIcon sx={{ fontSize: 13, color: c.ink56 }} />
                          <Typography sx={{ fontSize: 10.5, color: c.ink56, fontWeight: 700 }}>
                            {t('respondents.blocked')}
                          </Typography>
                        </Stack>
                      </Tooltip>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={r.recording_id == null}
                        startIcon={<ReplayRoundedIcon sx={{ fontSize: 14 }} />}
                        onClick={(e) => {
                          e.stopPropagation()
                          if (r.recording_id != null) onRetry(r.id, r.recording_id)
                        }}
                        sx={{ minWidth: 0, py: 0.1, px: 0.9, fontSize: 10.5, fontWeight: 700, textTransform: 'none', borderRadius: 1.5 }}
                      >
                        {t('respondents.resume')}
                      </Button>
                    </Stack>
                  ) : !r.has_data ? (
                    <Typography sx={{ fontSize: 10.5, color: c.ink48, flexShrink: 0, ml: 1 }}>
                      {t('respondents.noData')}
                    </Typography>
                  ) : undefined
                }
              />
            )
          })}
          {!respondents.length && (
            <Typography sx={{ color: c.ink56, fontSize: 13, py: 1 }}>{t('respondents.none')}</Typography>
          )}
        </Stack>
      </Stack>
    </Paper>
  )
})

/** One selectable row: avatar/badge · title+subtitle · radio tick (or status). */
function SelectRow({
  selectable,
  active,
  disabled,
  badge,
  title,
  subtitle,
  status,
  onClick,
}: {
  selectable?: boolean
  active?: boolean
  disabled?: boolean
  badge: React.ReactNode
  title: string
  subtitle?: string
  status?: React.ReactNode
  onClick?: () => void
}) {
  const theme = useTheme()
  const c = theme.affexy.colors
  return (
    <Box
      onClick={selectable ? onClick : undefined}
      role={selectable ? 'button' : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1,
        py: 0.85,
        borderRadius: 2,
        cursor: selectable ? 'pointer' : 'default',
        opacity: disabled ? 0.55 : 1,
        bgcolor: active ? c.coralSoft : c.gray50,
        border: `1px solid ${active ? c.coral : 'transparent'}`,
        transition: 'background-color .12s, border-color .12s',
        '&:hover': selectable ? { bgcolor: active ? c.coralSoft : c.gray100 } : {},
      }}
    >
      {badge}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: c.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ fontSize: 11.5, color: c.ink56, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {status ??
        (selectable && (
          <Box
            sx={{
              width: 20,
              height: 20,
              borderRadius: '50%',
              flexShrink: 0,
              display: 'grid',
              placeItems: 'center',
              bgcolor: active ? c.coral : 'transparent',
              border: `1.5px solid ${active ? c.coral : c.ink32}`,
              color: c.white,
            }}
          >
            {active && <CheckRoundedIcon sx={{ fontSize: 13 }} />}
          </Box>
        ))}
    </Box>
  )
}

function SectionTitle({ icon, title }: { icon: React.ReactNode; title: string }) {
  const theme = useTheme()
  const c = theme.affexy.colors
  return (
    <Stack direction="row" spacing={0.85} alignItems="center">
      <Box sx={{ color: c.coral, display: 'grid', placeItems: 'center' }}>{icon}</Box>
      <Typography sx={{ fontWeight: 800, fontSize: 14 }}>{title}</Typography>
    </Stack>
  )
}
